import { useEffect } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { config } from "../config/env";
import { isFleetScopedRole } from "../utils/authRoles";
import type { UserRole } from "../types/auth";

export function useAuth() {
  const {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    verifyToken,
    clearError,
  } = useAuthStore();

  useEffect(() => {
    const checkAuth = async () => {
      if (token && !user) {
        const isValid = await verifyToken();
        if (!isValid && config.enableDebugLogs) {
          console.warn("Token verification failed, user will be logged out");
        }
      }
    };

    checkAuth();
  }, [token, user, verifyToken]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const interval = setInterval(
      async () => {
        const isValid = await verifyToken();
        if (!isValid && config.enableDebugLogs) {
          console.warn("Token expired during periodic check");
        }
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [isAuthenticated, token, verifyToken]);

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    isAdmin: user?.role === "admin",
    isFleetScoped: isFleetScopedRole(user?.role),
    hasRole: (role: UserRole) => user?.role === role,
    checkAuth: verifyToken,
  };
}
